import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnimate } from 'framer-motion';
import styled from 'styled-components';
import { formatBytes } from '@frameio/utils/formatters';
import type { TextProps } from '@frameio/vapor';
import { Box, Text } from '@frameio/vapor';
import { IconCloudFill } from '@frameio/vapor-icons';

const ANIMATION_DISTANCE = 20;
const ANIMATION_DURATION = 0.2;
const GLOW_COLOR_VALUES = '114, 159, 255';
const MAX_GLOW_INTENSITY = 1; /** opacity */
const GLOW_DURATION = 0.5;

type StorageCounterProps = {
  align?: 'left' | 'right';
  value: number;
} & TextProps;

/**
 * This is used for the storage counter in the member access form when adding a new member provides additional storage.
 * It animates the current text down/out and the new text up/in to create a counter effect.
 * @param {string} [props.align='left'] - The alignment of the text to account for the text growing wider and avoiding layout shift
 * @param {number} props.value - The value to display
 * @param {...TextProps} props.rest - Additional props passed to the Vapor Text component
 */
export const StorageCounter = ({
  align = 'left',
  value,
  ...textProps
}: StorageCounterProps) => {
  const currentValue = useRef(value);
  const [newText, setNewText] = useState(formatBytes(value));
  const [currentText, setCurrentText] = useState(formatBytes(value));

  const [glowScope, animateGlow] = useAnimate();
  const [currentTextScope, animateCurrentText] = useAnimate();
  const [newTextScope, animateNewText] = useAnimate();

  const animateGlowInAndOut = useCallback(() => {
    animateGlow([
      [glowScope.current, { opacity: 0 }, { duration: 0 }],
      [
        glowScope.current,
        { opacity: MAX_GLOW_INTENSITY },
        { duration: GLOW_DURATION },
      ],
      [
        glowScope.current,
        { opacity: 0 },
        { delay: 0.2, duration: GLOW_DURATION },
      ],
    ]);
  }, [animateGlow, glowScope]);

  const increment = useCallback(async () => {
    animateGlowInAndOut();

    // Text to animate up/in
    setNewText(formatBytes(value));

    // Animate the current text down/out
    animateCurrentText([
      [currentTextScope.current, { opacity: 1, y: 0 }, { duration: 0 }],
      [
        currentTextScope.current,
        { opacity: 0, y: -ANIMATION_DISTANCE },
        { duration: ANIMATION_DURATION, ease: 'easeOut' },
      ],
    ]);

    // Animate the new text up/in
    await animateNewText([
      [
        newTextScope.current,
        { opacity: 0, y: ANIMATION_DISTANCE },
        { duration: 0 },
      ],
      [
        newTextScope.current,
        { opacity: 1, y: 0 },
        { duration: ANIMATION_DURATION, ease: 'easeOut' },
      ],
    ]);

    // Set the current text to the new value after the animation is complete
    setCurrentText(formatBytes(value));
  }, [
    animateCurrentText,
    animateGlowInAndOut,
    animateNewText,
    currentTextScope,
    newTextScope,
    value,
  ]);

  const decrement = useCallback(async () => {
    // Text to animate down/in
    setNewText(formatBytes(value));

    // Animate the current text down/out
    animateCurrentText([
      [currentTextScope.current, { opacity: 1, y: 0 }, { duration: 0 }],
      [
        currentTextScope.current,
        { opacity: 0, y: ANIMATION_DISTANCE },
        { duration: ANIMATION_DURATION, ease: 'easeOut' },
      ],
    ]);

    // Animate the new text down/in
    await animateNewText([
      [
        newTextScope.current,
        { opacity: 0, y: -ANIMATION_DISTANCE },
        { duration: 0 },
      ],
      [
        newTextScope.current,
        { opacity: 1, y: 0 },
        { duration: ANIMATION_DURATION, ease: 'easeOut' },
      ],
    ]);

    // Set the current text to the new value after the animation is complete
    setCurrentText(formatBytes(value));
  }, [
    animateCurrentText,
    animateNewText,
    currentTextScope,
    newTextScope,
    value,
  ]);

  useEffect(() => {
    if (value === currentValue.current) {
      animateGlowInAndOut();
      return;
    }

    if (value > currentValue.current) {
      increment();
    } else {
      decrement();
    }

    currentValue.current = value;
  }, [
    animateCurrentText,
    animateGlowInAndOut,
    animateNewText,
    currentTextScope,
    currentValue,
    decrement,
    increment,
    newTextScope,
    value,
  ]);

  return (
    <Box
      alignItems="center"
      display="flex"
      height="100%"
      justifyContent="space-between"
      minWidth={64}
      position="relative"
    >
      <CloudGlow>
        <StyledBackgroundBlur ref={glowScope} />
        <StyledIconCloudFill color="#729FFF" />
      </CloudGlow>
      <Box position="absolute" right="0">
        <Text ref={currentTextScope} {...textProps}>
          +{currentText}
        </Text>
        <Text
          ref={newTextScope}
          aria-hidden="true"
          style={{
            left: align === 'left' ? 0 : 'auto',
            position: 'absolute',
            right: align === 'right' ? 0 : 'auto',
            top: 0,
          }}
          {...textProps}
        >
          +{newText}
        </Text>
      </Box>
    </Box>
  );
};

const StyledIconCloudFill = styled(IconCloudFill)`
  filter: drop-shadow(0px 4px 12px rgba(${GLOW_COLOR_VALUES}, 0.3))
    drop-shadow(0px 2px 2px rgba(${GLOW_COLOR_VALUES}, 0.08));
  position: relative;
`;

const CloudGlow = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  left: 0;
  position: absolute;
  width: 100%;
`;

const StyledBackgroundBlur = styled.div`
  background-color: rgb(${GLOW_COLOR_VALUES});
  bottom: -37px;
  filter: blur(85px);
  height: 69px;
  left: 0;
  opacity: 0;
  position: absolute;
  right: -30px;
  width: 109px;
`;
